/**
 * EmpDetailsSection Component
 *
 * Employee details section with modern card-based UI
 * Includes IQAMA expiry date update functionality
 */

import React, { useState } from "react";
import { HiUser, HiCalendar, HiIdentification } from "react-icons/hi";
import CollapsibleSection from "../components/CollapsibleSection";
import nodeinstance from "../../../../../../nodeInstance";

const EmpDetailsSection = ({
  data,
  setTrigger,
  trigger,
  isLocalExit = false,
  baseEndpoint = '/mobilization',
  isPreviewMode = false,
}) => {
  const [newIqamaDate, setNewIqamaDate] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);

  const formatISODateToInputDate = (isoDate) => {
    if (!isoDate) return "";
    const date = new Date(isoDate);
    return date.toISOString().split("T")[0];
  };

  const formatDisplayDate = (isoDate) => {
    if (!isoDate) return "-----";
    return new Date(isoDate)
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      .split(" ")
      .join("-")
      .toUpperCase();
  };

  const handleDateChange = (e) => {
    setNewIqamaDate(e.target.value);
  };

  const token = localStorage.getItem("nodeToken");

  const updateIqamaDate = async (employeeId) => {
    if (!newIqamaDate) {
      alert("Please select a date to proceed.");
      return;
    }

    setIsUpdating(true);
    try {
      const reqObj = {
        employee_id: employeeId,
        Newiqamaexpiry: newIqamaDate,
      };

      const updateEndpoint = isLocalExit
        ? `${baseEndpoint}/UpdateExpiryDate`
        : `/mobilization/UpdateExpiryDate`;

      const response = await nodeinstance.put(updateEndpoint, reqObj, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 200) {
        alert("NEW IQAMA EXPIRY DATE updated successfully. The BALANCE TENURE AS PER NEW IQAMA EXPIRY DATE in Repatriation section will be updated automatically.");
        setTrigger(!trigger);
        setNewIqamaDate("");
      }
    } catch (error) {
      console.error("Error updating IQAMA expiry date:", error);
      alert("Failed to update IQAMA expiry date. Please try again.");
    } finally {
      setIsUpdating(false);
    }
  };

  const empData = data?.data?.[0];

  if (!empData) {
    return (
      <CollapsibleSection title="EMPLOYEE DETAILS" icon={<HiUser className="w-5 h-5" />}>
        <div className="text-center text-gray-500 py-8">
          No employee data available
        </div>
      </CollapsibleSection>
    );
  }

  const detailFields = [
    {
      label: "EMPLOYEE NAME",
      value: empData.name || "-",
      icon: <HiUser className="w-4 h-4 text-blue-600" />,
    },
    {
      label: "EMPLOYEE ID",
      value: empData.candidate_id || "-",
      icon: <HiIdentification className="w-4 h-4 text-blue-600" />,
    },
    {
      label: "NATIONALITY",
      value: empData.nationality || "-",
    },
    {
      label: "PASSPORT NUMBER",
      value: empData.passport_number || "-",
    },
    {
      label: "ACTUAL PROFESSION",
      value: empData.actual_profession || "-",
    },
    {
      label: "VISA PROFESSION",
      value: empData.visa_profession || "-",
    },
    {
      label: "ARRIVAL DATE",
      value: formatDisplayDate(empData.arrival_date),
      icon: <HiCalendar className="w-4 h-4 text-green-600" />,
    },
    {
      label: "IQAMA NUMBER",
      value: empData.iqama_number || "-",
    },
    {
      label: "IQAMA EXPIRY DATE",
      value: formatDisplayDate(empData.iqama_expiry_date_g),
      icon: <HiCalendar className="w-4 h-4 text-orange-600" />,
    },
    {
      label: "CURRENT CONTRACT END DATE",
      value: formatDisplayDate(empData.end_date),
      icon: <HiCalendar className="w-4 h-4 text-red-600" />,
    },
  ];

  return (
    <CollapsibleSection
      title="EMPLOYEE DETAILS"
      icon={<HiUser className="w-5 h-5" />}
      defaultCollapsed={true}
    >
      {/* Details Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {detailFields.map((field, index) => (
          <div
            key={index}
            className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-white rounded-lg border border-gray-200 hover:border-blue-300 transition-colors duration-200"
          >
            <div className="flex items-center space-x-2">
              {field.icon && <div>{field.icon}</div>}
              <span className="text-xs font-semibold text-gray-600 uppercase">
                {field.label}
              </span>
            </div>
            <span className="text-sm font-bold text-gray-900">
              {field.value}
            </span>
          </div>
        ))}
      </div>

      {/* New IQAMA Expiry Date Update Section - Always visible */}
      <div className="mt-6 p-5 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-2 border-blue-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-gray-800 uppercase flex items-center space-x-2">
              <HiCalendar className="w-5 h-5 text-blue-600" />
              <span>Update IQAMA Expiry Date</span>
            </h3>
          </div>

          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <label className="block text-xs font-semibold text-gray-700 mb-2">
                NEW IQAMA EXPIRY DATE
              </label>
              <input
                type="date"
                value={
                  newIqamaDate
                    ? newIqamaDate
                    : empData.Newiqamaexpiry != null
                    ? formatISODateToInputDate(empData.Newiqamaexpiry)
                    : ""
                }
                onChange={handleDateChange}
                disabled={isUpdating}
                className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            <button
              onClick={() => updateIqamaDate(empData.candidate_id)}
              disabled={isUpdating || !newIqamaDate}
              className="mt-7 px-6 py-2.5 bg-btn-red text-white font-semibold rounded-lg hover:bg-btn-red-hover focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg transform hover:scale-[1.02] active:scale-[0.98]"
            >
              {isUpdating ? (
                <span className="flex items-center space-x-2">
                  <svg
                    className="animate-spin h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <span>Updating...</span>
                </span>
              ) : (
                "Update Date"
              )}
            </button>
          </div>

          <p className="mt-3 text-xs text-gray-600 italic">
            💡 Updating this date will automatically recalculate the balance tenure in the Repatriation section.
          </p>
        </div>
    </CollapsibleSection>
  );
};

export default EmpDetailsSection;
